"use client";

import { type BaseIconName, Icon } from "@repo/ui/components/icon";
import { cn } from "@repo/ui/lib/utils";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@repo/ui/ui/collapsible";
import type { ToolUIPart } from "ai";
import type { ComponentProps, ReactNode } from "react";
import { isValidElement } from "react";

import { Spinner } from "../ui/spinner";
import { CodeBlock } from "./code-block";
import { Shimmer } from "./shimmer";

export type ToolProps = ComponentProps<typeof Collapsible>;

export const Tool = ({ className, ...props }: ToolProps) => (
  <Collapsible
    className={cn("not-prose w-full rounded-md", className)}
    {...props}
  />
);

export type ToolHeaderProps = {
  title?: string;
  /** Contextual detail rendered after the title (e.g. entity name). */
  subtitle?: ReactNode;
  type: ToolUIPart["type"];
  state: ToolUIPart["state"];
  icon?: BaseIconName;
  className?: string;
};

const isLoadingState = (state: ToolUIPart["state"]) =>
  state === "input-streaming" || state === "input-available";

/** Maps tool state to the left-side icon: spinner while loading, tool icon otherwise. */
const _getStateIcon = (
  state: ToolUIPart["state"],
  icon: BaseIconName | undefined,
): ReactNode => {
  if (isLoadingState(state)) return <Spinner className="size-4 shrink-0" />;

  const errorStates: ReadonlySet<ToolUIPart["state"]> = new Set([
    "output-error",
    "output-denied",
  ]);

  return (
    <Icon
      name={errorStates.has(state) ? "octagon-cross" : (icon ?? "dot-grid")}
      className={cn(
        "size-3.5 shrink-0",
        errorStates.has(state) ? "text-red-600" : "text-muted-foreground",
      )}
    />
  );
};

export const ToolHeader = ({
  className,
  title,
  subtitle,
  type,
  state,
  icon,
  ...props
}: ToolHeaderProps) => {
  const loading = isLoadingState(state);
  const resolvedTitle = title ?? type.split("-").slice(1).join("-");

  return (
    <CollapsibleTrigger
      className={cn(
        "flex w-full items-center justify-between gap-4 p-3",
        "group p-0",
        className,
      )}
      // disabled
      {...props}
    >
      <div className="flex min-w-0 items-center gap-1">
        {/* {getStateIcon(state, icon)} */}
        {loading ? (
          <Shimmer as="span" className="shrink-0 font-medium text-xs">
            {resolvedTitle}
          </Shimmer>
        ) : (
          <span className="shrink-0 font-medium text-muted-foreground text-xs">
            {resolvedTitle}
          </span>
        )}
        {subtitle && (
          <span className="truncate text-muted-foreground/60 text-xs">
            {subtitle}
          </span>
        )}
      </div>
      {/* {!loading && (
        <>
          <Icon
            name="chevron-double-down-up"
            className="hidden size-3.5 text-muted-foreground group-data-panel-open:block"
          />
          <Icon
            name="chevron-double-up-down"
            className="size-3.5 text-muted-foreground group-data-panel-open:hidden"
          />
        </>
      )} */}
    </CollapsibleTrigger>
  );
};

export type ToolContentProps = ComponentProps<typeof CollapsibleContent>;

export const ToolContent = ({ className, ...props }: ToolContentProps) => (
  <CollapsibleContent
    className={cn(
      "data-[state=closed]:fade-out-0 data-[state=closed]:slide-out-to-top-2 data-[state=open]:slide-in-from-top-2 text-popover-foreground outline-none data-[state=closed]:animate-out data-[state=open]:animate-in",
      className,
    )}
    {...props}
  />
);

export type ToolInputProps = ComponentProps<"div"> & {
  input: ToolUIPart["input"];
};

export const ToolInput = ({ className, input, ...props }: ToolInputProps) => (
  <div className={cn("space-y-2 overflow-hidden p-4", className)} {...props}>
    <h4 className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
      Parameters
    </h4>
    <div className="rounded-md bg-muted/50">
      <CodeBlock code={JSON.stringify(input, null, 2)} language="json" />
    </div>
  </div>
);

export type ToolOutputProps = ComponentProps<"div"> & {
  output: ToolUIPart["output"];
  errorText: ToolUIPart["errorText"];
};

export const ToolOutput = ({
  className,
  output,
  errorText,
  ...props
}: ToolOutputProps) => {
  if (!(output || errorText)) {
    return null;
  }

  let Output = <div>{output as ReactNode}</div>;

  if (typeof output === "object" && !isValidElement(output)) {
    Output = (
      <CodeBlock code={JSON.stringify(output, null, 2)} language="json" />
    );
  } else if (typeof output === "string") {
    Output = <CodeBlock code={output} language="json" />;
  }

  return (
    <div className={cn("space-y-2 p-4", className)} {...props}>
      <h4 className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
        {errorText ? "Error" : "Result"}
      </h4>
      <div
        className={cn(
          "overflow-x-auto rounded-md text-xs [&_table]:w-full",
          errorText
            ? "bg-destructive/10 text-destructive"
            : "bg-muted/50 text-foreground",
        )}
      >
        {errorText && <div>{errorText}</div>}
        {Output}
      </div>
    </div>
  );
};
